import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Check, X, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { Lead } from "@/lib/types";
import { ago, shortDate, US_STATES } from "@/lib/format";
import { Button, Card, PageHeader, Loading, Empty, Field, Tag, AiTag, toast } from "@/components/ui";

type Status = "pending" | "accepted" | "rejected";
const FIELDS: { key: keyof Lead; label: string; wide?: boolean }[] = [
  { key: "company", label: "Company" }, { key: "contactName", label: "Contact" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
  { key: "title", label: "Title" }, { key: "businessType", label: "What they make" }, { key: "erp", label: "ERP / shop software" }, { key: "address", label: "Address" },
  { key: "city", label: "City" }, { key: "stateCode", label: "State" }, { key: "postalCode", label: "ZIP" }, { key: "interest", label: "What they want", wide: true },
];

export function LeadsPage() {
  const [status, setStatus] = useState<Status>("pending");
  const { data, loading, refetch } = useQuery<Lead[]>(`leads:${status}`, () => api.get<Lead[]>(`/leads?status=${status}`), [status]);
  const changed = () => { refetch(); invalidate("leads"); invalidate("badges"); invalidate("board"); invalidate("accounts"); invalidate("pipeline"); };
  return (
    <>
      <PageHeader title="Leads" sub={status === "pending" && data ? `${data.length} waiting` : undefined}>
        <div className="seg">{(["pending", "accepted", "rejected"] as Status[]).map((s) => <button key={s} className={status === s ? "active" : ""} onClick={() => setStatus(s)}>{s[0].toUpperCase() + s.slice(1)}</button>)}</div>
      </PageHeader>
      <div className="page">
        <div className="small muted-2 mb-12">Form fills from your lead-source addresses land here. Claude fills in what it can read; check it, fix anything wrong, then Accept to create the account, contact and a deal at Lead, or Reject. Sources are set under Admin → Inboxes.</div>
        {loading && !data ? <Loading /> : (data ?? []).length === 0 ? <Card><Empty>{status === "pending" ? "No leads waiting. New form fills show up here within a minute of arriving." : `No ${status} leads yet.`}</Empty></Card> : (
          <div className="col gap-16">{(data ?? []).map((l) => <LeadCard key={l.id} lead={l} onChanged={changed} />)}</div>
        )}
      </div>
    </>
  );
}

function LeadCard({ lead, onChanged }: { lead: Lead; onChanged: () => void }) {
  const nav = useNavigate();
  const [f, setF] = useState<Record<string, string>>(Object.fromEntries(FIELDS.map(({ key }) => [key, (lead[key] as string | null) ?? ""])));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState<string>("");
  const pending = lead.status === "pending";
  const set = (k: string, v: string) => { setF((x) => ({ ...x, [k]: v })); setDirty(true); };
  const save = async () => { await api.patch(`/leads/${lead.id}`, f); setDirty(false); };
  const accept = async () => {
    setBusy(true);
    try {
      if (dirty) await save();
      const r = await api.post<{ accountId: string; created: boolean }>(`/leads/${lead.id}/accept`, { existingAccountId: existing || null });
      toast(r.created ? "Account created and added to the pipeline at Lead" : "Lead added to the existing account");
      onChanged();
      nav(`/accounts/${r.accountId}`);
    } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = prompt("Why reject? (optional: spam, test, not a fit…)", "");
    if (reason === null) return;
    setBusy(true);
    try { await api.post(`/leads/${lead.id}/reject`, { reason: reason || null }); toast("Lead rejected"); onChanged(); } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  const conf = lead.confidence ?? 0;
  return (
    <Card title={<span className="row gap-8"><span>{f.company || f.contactName || "Unnamed lead"}</span><AiTag />{conf < 0.6 && <Tag color="yellow">Low confidence</Tag>}{lead.status !== "pending" && <Tag color={lead.status === "accepted" ? "green" : "outline"}>{lead.status === "accepted" ? "Accepted" : "Rejected"}</Tag>}</span>}
      actions={<span className="small muted">{ago(lead.createdAt)} ago{lead.source ? <> · <Link to={`/inbox/${lead.threadId}`} className="row gap-4" style={{ display: "inline-flex" }}><Mail size={12} /> {lead.source.subject || "form email"}</Link></> : null}</span>}>
      {lead.summary && <div className="card-body small" style={{ color: "var(--text-body)", paddingBottom: 0 }}>{lead.summary}</div>}
      {lead.matches.length > 0 && pending && (
        <div className="card-body">
          <div className="cal-warn" style={{ marginBottom: 0 }}>
            Possible match: {lead.matches.map((m) => <Link key={m.id} to={`/accounts/${m.id}`} style={{ color: "inherit", fontWeight: 600 }}>{m.name} ({m.number})</Link>).reduce<React.ReactNode[]>((acc, el, i) => (i ? [...acc, ", ", el] : [el]), [])} — {lead.matches[0].why}.
            <label className="row gap-6" style={{ marginTop: 6 }}><input type="checkbox" checked={!!existing} onChange={(e) => setExisting(e.target.checked ? lead.matches[0].id : "")} /> Add this lead to {lead.matches[0].name} instead of creating a new account</label>
          </div>
        </div>
      )}
      <div className="card-body">
        <div className="form-grid">
          {FIELDS.map(({ key, label, wide }) => (
            <Field key={key} label={label} className={wide ? "span-2" : undefined}>
              {key === "stateCode" ? (
                <select className="select" disabled={!pending} value={f[key]} onChange={(e) => set(key, e.target.value)}><option value="">—</option>{US_STATES.map((s) => <option key={s}>{s}</option>)}</select>
              ) : wide ? <textarea className="textarea" style={{ minHeight: 56 }} readOnly={!pending} value={f[key]} onChange={(e) => set(key, e.target.value)} />
              : <input className="input" readOnly={!pending} value={f[key]} onChange={(e) => set(key, e.target.value)} />}
            </Field>
          ))}
        </div>
        {lead.message && <details className="mt-8"><summary className="small muted" style={{ cursor: "pointer" }}>Message from the form</summary><div className="prose small mt-8" style={{ whiteSpace: "pre-wrap", color: "var(--text-body)" }}>{lead.message}</div></details>}
      </div>
      <div className="card-body row gap-8 wrap" style={{ borderTop: "1px solid var(--border-soft)" }}>
        {pending ? (
          <>
            <Button variant="primary" disabled={busy || (!f.company && !f.contactName)} onClick={accept}><Check size={13} /> {existing ? "Add to existing account" : "Accept and create account"}</Button>
            <Button variant="danger" disabled={busy} onClick={reject}><X size={13} /> Reject</Button>
            {dirty && <Button disabled={busy} onClick={async () => { await save(); toast("Saved"); }}>Save edits</Button>}
            <span className="xs muted ml-auto">Confidence {(conf * 100).toFixed(0)}%</span>
          </>
        ) : (
          <span className="small muted">{lead.status === "accepted" && lead.account ? <>Account <Link to={`/accounts/${lead.account.id}`}>{lead.account.name} · {lead.account.number}</Link></> : lead.rejectReason ? `Reason: ${lead.rejectReason}` : "No reason given"} · by {lead.reviewedByName ?? "?"} · {shortDate(lead.reviewedAt)}</span>
        )}
      </div>
    </Card>
  );
}
